import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

// Super Admin always gets all (locked, no column shown).
// The set of columns shown here is now driven by the module's Access scope —
// only positions that have access will appear as editable columns.
const POSITION_LABELS = {
  manager: 'Manager',
  sales_admin: 'Sales Admin',
  sales: 'Sales',
  dispatcher: 'Dispatcher',
  driver: 'Driver',
  truck_maintenance: 'Truck Maintenance',
  sign_technician: 'Sign Technician',
};
const POSITION_SORT_ORDER = [
  'manager', 'sales_admin', 'sales', 'dispatcher',
  'driver', 'truck_maintenance', 'sign_technician',
];

export default function ModulePermissionsTab({ moduleKey, scope, scopeLoading }) {
  const [catalog, setCatalog] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const data = await api.getModulePermissions(moduleKey);
      setCatalog({
        permissions: data.catalog,
        positionDefaults: data.positionDefaults || data.roleDefaults || {},
        managerOverlay: data.managerOverlay || {},
      });
      setOverrides(data.overrides || []);
    } catch (err) {
      console.error('Failed to load module permissions:', err);
    } finally {
      setLoading(false);
    }
  }, [moduleKey]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading || !catalog || scopeLoading) return <p>Loading permissions...</p>;

  const permKeys = Object.keys(catalog.permissions);

  // Derive the position columns from the module Access scope.
  // Super Admin is never shown as a column (always locked true).
  const scopePositionKeys = (scope?.positions || [])
    .map(p => p.key)
    .filter(k => k && k !== 'super_admin');
  const POSITIONS = Array.from(new Set(scopePositionKeys))
    .sort((a, b) => POSITION_SORT_ORDER.indexOf(a) - POSITION_SORT_ORDER.indexOf(b));

  if (POSITIONS.length === 0) {
    return (
      <div style={{
        padding: 20, borderRadius: 8,
        background: 'rgba(218, 165, 32, 0.08)',
        border: '1px solid rgba(218, 165, 32, 0.25)',
        color: 'var(--text-primary)',
      }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Configure Access first</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          No positions have been granted access to this module yet. Open the <strong>Access</strong> tab
          and pick a team, position, or user. Those selections determine who you can configure here.
        </div>
      </div>
    );
  }

  // Get effective value for a position+permission: check overrides first, then defaults
  function getEffective(position, perm) {
    const override = overrides.find(
      o => o.grant_type === 'position' && o.grant_target === position && o.permission === perm
    );
    if (override) return { value: override.granted === 1, isOverride: true };
    const def = catalog.positionDefaults[position]?.[perm];
    return { value: def === true, isOverride: false };
  }

  function togglePermission(position, perm) {
    const { value, isOverride } = getEffective(position, perm);
    const defaultValue = catalog.positionDefaults[position]?.[perm] === true;
    const newValue = !value;

    if (newValue === defaultValue && isOverride) {
      setOverrides(overrides.filter(
        o => !(o.grant_type === 'position' && o.grant_target === position && o.permission === perm)
      ));
      return;
    }

    const existing = overrides.findIndex(
      o => o.grant_type === 'position' && o.grant_target === position && o.permission === perm
    );
    if (existing >= 0) {
      const updated = [...overrides];
      updated[existing] = { ...updated[existing], granted: newValue ? 1 : 0 };
      setOverrides(updated);
    } else {
      setOverrides([...overrides, {
        permission: perm,
        grant_type: 'position',
        grant_target: position,
        granted: newValue ? 1 : 0,
      }]);
    }
  }

  async function savePermissions() {
    setSaving(true);
    try {
      const allOverrides = overrides.filter(o => o.grant_type === 'position');
      const result = await api.updateModulePermissions(moduleKey, allOverrides);
      setOverrides(result.overrides || overrides);
    } catch (err) {
      alert('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  }

  // Show manager overlay perms separately
  const managerOverlayPerms = Object.keys(catalog.managerOverlay).filter(k => catalog.managerOverlay[k]);

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 16 }}>
        Super Admin always has full access. Configure what each <strong>position</strong> can do in this module.
        <span style={{ color: 'var(--brand-gold)', fontWeight: 600 }}> Gold cells</span> indicate overrides from defaults.
      </p>

      <h3 style={{ fontSize: '0.95rem', marginBottom: 8 }}>Position Defaults</h3>
      <div className="table-wrapper">
        <table className="data-table" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', minWidth: 180 }}>Permission</th>
              {POSITIONS.map(pos => (
                <th key={pos} style={{ textAlign: 'center', width: 100 }}>{POSITION_LABELS[pos]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {permKeys.map(perm => (
              <tr key={perm}>
                <td>
                  <div style={{ fontWeight: 600 }}>{catalog.permissions[perm].label}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {catalog.permissions[perm].description}
                  </div>
                </td>
                {POSITIONS.map(pos => {
                  const { value, isOverride } = getEffective(pos, perm);
                  return (
                    <td key={pos} style={{
                      textAlign: 'center',
                      background: isOverride ? 'rgba(218, 165, 32, 0.08)' : undefined,
                    }}>
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={() => togglePermission(pos, perm)}
                        style={{ cursor: 'pointer', width: 18, height: 18 }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {managerOverlayPerms.length > 0 && (
        <div style={{ marginTop: 24, padding: 16, background: 'var(--surface-raised)', borderRadius: 8 }}>
          <h3 style={{ fontSize: '0.95rem', marginBottom: 8 }}>Manager Overlay</h3>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
            These extra permissions are granted to anyone marked as <strong>Manager</strong> on this module's manager team
            (configured in Module Setup → Access). They apply on top of the position defaults above.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {managerOverlayPerms.map(perm => (
              <span key={perm} style={{
                padding: '4px 10px',
                background: 'var(--brand-gold-soft, rgba(218, 165, 32, 0.15))',
                color: 'var(--brand-gold-dark, #b8860b)',
                borderRadius: 12, fontSize: '0.8rem', fontWeight: 600,
              }}>
                + {catalog.permissions[perm]?.label || perm}
              </span>
            ))}
          </div>
        </div>
      )}

      <button className="btn btn-gold" style={{ marginTop: 16 }} onClick={savePermissions} disabled={saving}>
        {saving ? 'Saving...' : 'Save Permissions'}
      </button>
    </div>
  );
}
